/**
 * What fits on the bottom line of a conversation row, and what gets cut.
 *
 * THE ROW IS 270 PIXELS WIDE AND THAT IS THE WHOLE PROBLEM. The design sheet
 * draws a pill line with room for about two of them, and most of its rows carry
 * one. This product can put more than twice that on the same line: the
 * classifier's read with its confidence beside it, a brand chip in the
 * cross-brand view, the sending account's own label, a "waiting on you" badge,
 * and now however many of the workspace's own labels somebody has filed the
 * thread under. Six pills in 270px wrap to three lines and stop being scannable,
 * which costs more than the information they carry: a list you have to read
 * rather than scan is a list with no advantage over opening each thread.
 *
 * So this picks. It is a pure function rather than a chain of `&&`s in the row
 * because the priority is a decision with a cost, and a decision with a cost
 * belongs somewhere it can be argued with and tested.
 *
 * PRIORITY IS NOT THE SAME LIST AS DISPLAY ORDER, and separating them is what
 * lets the row match the sheet without the sheet's order deciding what survives.
 * The sheet's rows read "Positive", then "Action required" beside it, so that is
 * how they are drawn. But if only one can fit, the one that survives is the one
 * that says a person is needed, not the one that says how the machine read it.
 * Keeping those as two lists costs a few lines here and stops the row from
 * dropping its only call to action to make room for a label.
 *
 * WHAT WAS DROPPED AND WHERE IT WENT.
 *
 *   - The sending account's label. It was a plain grey badge on every row, and
 *     on the ordinary brand with one account per platform it repeated the
 *     platform glyph already sitting beside the name. It is still the "Sent
 *     from" control in the filter panel, and the thread pane names the account
 *     the reply would go out from, which is the moment it actually matters.
 *   - The classifier's confidence percentage. A number nobody acts on in a
 *     list. The chip still carries the unsure styling, the row still carries the
 *     warning edge, and the percentage itself is a click away in the thread.
 *
 * The brand chip is NOT handled here and does not count against the limit. It is
 * identity rather than description, it only ever appears in the admin
 * cross-brand view, and acting in the wrong brand is the expensive mistake on
 * that screen, so it keeps its place on the row unconditionally.
 */
import type { Tone } from '../../lib/labels'
import { label, tone } from '../../lib/labels'
import { appliedByAutoLabel } from './autolabel'
import { toDate } from '../../lib/format'
import type { ClassificationLabel, ConversationLabel } from '../../lib/types'
import { APPROXIMATE_DATE_TITLE, threadTimeOf, type MessageTime } from './transcript'

/**
 * The inbox's own word for a classifier label.
 *
 * THE DESIGN NAMES THREE OF THEM DIFFERENTLY and the owner chose the design's
 * words: Positive, Negative and Wrong contact. The shared table in lib/labels was
 * then changed to the same words, because the dashboard and the needs-a-person
 * queue read it too, and one reply named two ways depending on the screen is the
 * worse outcome. So this override now agrees with the shared table. It stays as
 * the inbox's one place to read the word from, which every inbox surface (the
 * rail, the row's pill, the thread's chip) already does, and a label with no
 * override keeps the shared word, so a label added to the enum still has a name.
 *
 * Not to be confused with the reasons for ending a sequence in ThreadActions,
 * where "Not interested" and "Wrong person" are why a person stopped, a separate
 * vocabulary from what the classifier read.
 */
const INBOX_CLASSIFICATION_WORDS: Partial<Record<ClassificationLabel, string>> = {
  POSITIVE: 'Positive',
  NEGATIVE: 'Negative',
  WRONG_PERSON: 'Wrong contact',
}

export function inboxClassificationWord(classification: ClassificationLabel): string {
  return INBOX_CLASSIFICATION_WORDS[classification] ?? label.classification(classification)
}

/** As many as the sheet's row has room for. */
export const ROW_PILL_LIMIT = 2

export type RowPillKind = 'attention' | 'classification' | 'system-label' | 'label'

export interface RowPill {
  key: string
  kind: RowPillKind
  text: string
  tone: Tone
  /** The colour the person chose, set only on one of the workspace's own labels. */
  colour?: string
  /** A workspace label Auto-label put on the thread (0153). The tag carries its small mark. */
  autoLabelled?: boolean
}

export interface RowPillThread {
  classification?: ClassificationLabel | null
  needsAttention?: boolean
  labels?: ConversationLabel[]
}

/** Said on the row, and the same words the rail's own row uses. */
export const ROW_ATTENTION_TEXT = 'Action required'

export interface RowPillResult {
  /** Drawn, in the order the sheet draws them. */
  pills: RowPill[]
  /** Cut for space, in priority order, so the overflow marker can name them. */
  hidden: RowPill[]
  /** The overflow marker's tooltip. Empty when nothing was cut. */
  hiddenTitle: string
}

const DISPLAY_ORDER: RowPillKind[] = ['classification', 'attention', 'system-label', 'label']

export function rowPills(
  thread: RowPillThread,
  options: {
    limit?: number
    /**
     * Which of the thread's labels are the ones every workspace starts with.
     *
     * The row's own label entries do not say, so the page hands over the ids the
     * counts marked as system. Closed and Meeting booked are the outcome of the
     * conversation, which is worth more on a 270px row than a filing label
     * somebody invented, so they are kept ahead of the custom ones when the line
     * runs out of room. An absent set treats every label as custom, which is the
     * order this function had before system labels existed.
     */
    systemLabelIds?: ReadonlySet<string>
  } = {},
): RowPillResult {
  const limit = options.limit ?? ROW_PILL_LIMIT
  const systemIds = options.systemLabelIds

  /*
   * Priority order. Attention first because it is the only one of the three
   * that asks for something, classification second because it is on nearly
   * every row and is what the sheet leads with, then the system labels because
   * they record where the conversation ended up, and the workspace's custom
   * labels last because they are a filing decision somebody already made and
   * the rail two columns to the left is the place that question gets asked.
   */
  const byPriority: RowPill[] = []

  if (thread.needsAttention === true) {
    byPriority.push({
      key: 'attention',
      kind: 'attention',
      text: ROW_ATTENTION_TEXT,
      tone: 'warning',
    })
  }

  if (thread.classification) {
    byPriority.push({
      key: `classification:${thread.classification}`,
      kind: 'classification',
      text: inboxClassificationWord(thread.classification),
      tone: tone.classification(thread.classification),
    })
  }

  const labels = thread.labels ?? []
  const isSystem = (entry: ConversationLabel) => systemIds?.has(entry.id) === true
  for (const entry of [...labels.filter(isSystem), ...labels.filter((entry) => !isSystem(entry))]) {
    byPriority.push({
      key: `label:${entry.id}`,
      kind: isSystem(entry) ? 'system-label' : 'label',
      text: entry.name,
      tone: 'neutral',
      colour: entry.colour,
      ...(appliedByAutoLabel(entry) ? { autoLabelled: true } : {}),
    })
  }

  const kept = limit <= 0 ? [] : byPriority.slice(0, limit)
  const hidden = limit <= 0 ? [...byPriority] : byPriority.slice(limit)

  const pills = [...kept].sort(
    (a, b) => DISPLAY_ORDER.indexOf(a.kind) - DISPLAY_ORDER.indexOf(b.kind),
  )

  return { pills, hidden, hiddenTitle: hiddenTitleFor(hidden) }
}

/**
 * The words behind the "+2".
 *
 * A bare count on a row is a promise that something is there without saying
 * what, which is the kind of marker people learn to ignore. Naming them costs
 * one attribute and makes the marker worth hovering.
 */
export function hiddenTitleFor(hidden: readonly RowPill[]): string {
  if (hidden.length === 0) return ''
  return `Also on this thread: ${hidden.map((pill) => pill.text).join(', ')}`
}

// ---------------------------------------------------------------------------
// The time on the row's first line
// ---------------------------------------------------------------------------

/** The year a moment falls in, on the clock the row is drawn in. */
function yearOf(date: Date, timeZone?: string): string {
  return new Intl.DateTimeFormat('en-US', { year: 'numeric', ...(timeZone === undefined ? {} : { timeZone }) }).format(date)
}

/**
 * The design's row date, pinned to one format: "Jul 31, 09:07 PM", with the
 * year added when it is not this year ("Jul 31, 2025, 09:07 PM"), because a
 * date with no year that is not this year's reads as this year's.
 *
 * PINNED TO en-US ON PURPOSE. Everything else in this product formats in the
 * browser's locale, and on the row that meant a width nobody could plan the
 * name around: a 24-hour locale drops the AM/PM and a day-first one reorders the
 * parts, so the space the name had depended on who was looking. The owner chose
 * the design's format, which also makes the row's width a number that can be
 * measured once.
 *
 * `timeZone` exists for the tests and nothing else. The row shows the viewer's
 * own clock, which is what leaving it out does.
 */
export function formatRowDate(date: Date, timeZone?: string, now: Date = new Date()): string {
  const otherYear = yearOf(date, timeZone) !== yearOf(now, timeZone)
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    ...(otherYear ? { year: 'numeric' as const } : {}),
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    ...(timeZone === undefined ? {} : { timeZone }),
  }).format(date)
}

/**
 * The day alone, "Sep 21", with the year when it is not this year: the most a
 * stamp a phone read off a screen can honestly claim on a row.
 */
export function formatRowDay(date: Date, timeZone?: string, now: Date = new Date()): string {
  const otherYear = yearOf(date, timeZone) !== yearOf(now, timeZone)
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    ...(otherYear ? { year: 'numeric' as const } : {}),
    ...(timeZone === undefined ? {} : { timeZone }),
  }).format(date)
}

export interface RowTime {
  kind: MessageTime['kind']
  /** What the row draws. */
  text: string
  /** The row's tooltip, which carries the whole sentence the text was cut from. */
  title: string
  /** The ISO stamp for a `<time>` element. Only on an exact time. */
  dateTime?: string
}

/** Said on the row where a thread has no time at all. The full sentence is in the title. */
export const ROW_NO_TIME = 'No time'

export function rowTimeOf(
  thread: { lastMessageAt?: string | null; lastMessageAtIsApproximate?: boolean },
  now: Date = new Date(),
  timeZone?: string,
): RowTime {
  const when = threadTimeOf(thread, now)
  const date = toDate(thread.lastMessageAt)
  if (when.kind === 'unknown' || date === null) {
    return { kind: 'unknown', text: ROW_NO_TIME, title: when.text }
  }
  /*
    A STAMP A PHONE READ OFF A SCREEN IS THE DAY ALONE, "Sep 29", with
    "Approximate, read off the phone" in its tooltip, and never "about Sep 29":
    the owner's brief, round four. The row said "about 5h", then "about Sep
    29". The day with no time of day is still the hedge, because every exact
    date in the column beside it carries its time, and it never reaches a
    `<time>` element that would publish a moment.
  */
  if (when.kind === 'approximate') {
    return { kind: 'approximate', text: formatRowDay(date, timeZone, now), title: APPROXIMATE_DATE_TITLE }
  }
  return {
    kind: 'exact',
    text: formatRowDate(date, timeZone, now),
    title: formatRowDate(date, timeZone, now),
    dateTime: date.toISOString(),
  }
}

// ---------------------------------------------------------------------------
// The reminder on a row
// ---------------------------------------------------------------------------

/**
 * Where a thread's reminder stands: due (its moment has come, so the thread is
 * waiting on somebody) or scheduled (still ahead). Null when there is none.
 *
 * The reference draws a small alarm beside the date on a row with a reminder,
 * and its filters split the same two states. The list route cannot split them
 * (the Reminders tab is every thread with a reminder at all), so the split is
 * drawn on the row, where it is read off the one row's own date and claims
 * nothing about the rest of the list.
 */
export type ReminderState = 'due' | 'scheduled'

export function reminderStateOf(
  remindAt: string | null | undefined,
  now: Date = new Date(),
): ReminderState | null {
  const date = toDate(remindAt)
  if (date === null) return null
  return date.getTime() <= now.getTime() ? 'due' : 'scheduled'
}

/**
 * The reminder in words, for a tooltip. It names a date and a tab, never an
 * alarm: nothing fires on `remind_at`, it is a filter.
 */
export function reminderTitle(
  remindAt: string | null | undefined,
  now: Date = new Date(),
  timeZone?: string,
): string | null {
  const date = toDate(remindAt)
  const state = reminderStateOf(remindAt, now)
  if (date === null || state === null) return null
  const when = formatRowDate(date, timeZone, now)
  return state === 'due' ? `Reminder due since ${when}` : `Reminder set for ${when}`
}

// ---------------------------------------------------------------------------
// Read state, written into a cached thread
// ---------------------------------------------------------------------------

/**
 * A cached thread with its read state replaced.
 *
 * The toolbar's Unread button reads this, and on a cold load it read the value
 * from BEFORE the automatic mark-read for the whole 6.5 seconds the browser pass
 * sampled, while the server already held the thread as read. The thread GET and
 * the mark-read POST start together and the GET answered with the old value. So
 * the hooks write the new value straight into the cache, and this is that write.
 *
 * An absent thread stays absent. Inventing one from two fields would hand the
 * pane a conversation with no messages in it.
 */
export function withReadState<T extends { unread: boolean; readAt: string | null }>(
  cached: T | undefined,
  next: { unread: boolean; readAt: string | null },
): T | undefined {
  if (cached === undefined) return undefined
  return { ...cached, unread: next.unread, readAt: next.readAt }
}
